import { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import {
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconPlayerPlay,
  IconRefresh,
  IconAlertCircle,
  IconLoader2,
  IconCalendar,
  IconCalendarTime,
  IconCloud,
  IconClockCog,
  IconHandStop,
  IconBolt,
} from '@tabler/icons-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import { StatCard } from '../components/cards';

interface JobExecution {
  id: number;
  startedAt: string;
  completedAt: string | null;
  status: 'success' | 'failed' | 'running' | 'cancelled';
  errorMessage: string | null;
  temporalRunId: string | null;
  metadata: Record<string, any> | null;
}

interface Job {
  id: number;
  name: string;
  description: string | null;
  schedule: string | null;
  jobType: 'scheduler' | 'cron' | 'temporal' | 'manual';
  enabled: boolean;
  temporalWorkflowId: string | null;
  serviceName: string | null;
  methodName: string | null;
  createdAt: string;
  updatedAt: string;
  latestExecution: JobExecution | null;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch {
    return '—';
  }
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return 'Running...';
  try {
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const diff = end.getTime() - start.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  } catch {
    return '—';
  }
}

function getStatusIcon(status: string, size: number = 20) {
  const iconProps = { size, style: { display: 'inline' } as const };
  switch (status) {
    case 'success':
      return <IconCircleCheck {...iconProps} color="#2e7d32" />;
    case 'failed':
      return <IconCircleX {...iconProps} color="#d32f2f" />;
    case 'running':
      return <IconLoader2 {...iconProps} color="#1976d2" className="animate-spin" />;
    case 'cancelled':
      return <IconAlertCircle {...iconProps} color="#757575" />;
    default:
      return <IconClock {...iconProps} color="#9e9e9e" />;
  }
}

function getStatusVariant(status: string): 'default' | 'destructive' | 'secondary' | 'outline' {
  switch (status) {
    case 'success':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'running':
      return 'secondary';
    default:
      return 'outline';
  }
}

function getJobTypeLabel(jobType: string): string {
  switch (jobType) {
    case 'scheduler':
      return 'Cloud Scheduler';
    case 'cron':
      return 'Cron';
    case 'temporal':
      return 'Temporal';
    case 'manual':
      return 'Manual';
    default:
      return jobType;
  }
}

function getJobTypeIcon(jobType: string, size: number = 16) {
  const iconProps = { size, style: { display: 'inline', marginRight: 4 } as const };
  switch (jobType) {
    case 'scheduler':
      return <IconCloud {...iconProps} />;
    case 'cron':
      return <IconClockCog {...iconProps} />;
    case 'temporal':
      return <IconBolt {...iconProps} />;
    case 'manual':
      return <IconHandStop {...iconProps} />;
    default:
      return <IconClock {...iconProps} />;
  }
}

export function JobsPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [jobHistory, setJobHistory] = useState<JobExecution[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await api.get('/jobs');
      if (resp.data?.success && Array.isArray(resp.data.jobs)) {
        setJobs(resp.data.jobs);
        setLastUpdatedAt(new Date());
      } else {
        setError('Invalid response format');
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to load jobs';
      setError(errorMessage);
      console.error('Error loading jobs:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadHistory = async (jobId: number) => {
    setLoadingHistory(true);
    try {
      const resp = await api.get(`/jobs/${jobId}/history?limit=50`);
      if (resp.data?.success && Array.isArray(resp.data.executions)) {
        setJobHistory(resp.data.executions);
      }
    } catch (e: any) {
      console.error('Error loading job history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTriggerJob = async (jobId: number) => {
    setTriggeringJob(jobId);
    try {
      await api.post(`/jobs/${jobId}/trigger`);
      toast.success('Job triggered successfully');
      await handleRefresh();
      if (selectedJob?.id === jobId) {
        await handleLoadHistory(jobId);
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Failed to trigger job';
      toast.error(errorMessage);
      console.error('Error triggering job:', e);
    } finally {
      setTriggeringJob(null);
    }
  };

  useEffect(() => {
    handleRefresh();
    const interval = setInterval(handleRefresh, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedJob) {
      handleLoadHistory(selectedJob.id);
    }
  }, [selectedJob]);

  const filteredJobs = useMemo(() => {
    if (!searchTerm) return jobs;
    const term = searchTerm.toLowerCase();
    return jobs.filter(
      (job) =>
        job.name.toLowerCase().includes(term) ||
        job.description?.toLowerCase().includes(term) ||
        job.serviceName?.toLowerCase().includes(term),
    );
  }, [jobs, searchTerm]);

  const stats = useMemo(() => {
    const total = jobs.length;
    const enabled = jobs.filter((j) => j.enabled).length;
    const disabled = total - enabled;
    const success = jobs.filter((j) => j.latestExecution?.status === 'success').length;
    const failed = jobs.filter((j) => j.latestExecution?.status === 'failed').length;
    const running = jobs.filter((j) => j.latestExecution?.status === 'running').length;
    return { total, enabled, disabled, success, failed, running };
  }, [jobs]);

  if (isLoading && jobs.length === 0) {
    return <PageLoader message="Loading jobs..." />;
  }

  return (
    <div>
      <PageHeader
        title="Job Management"
        subtitle="Monitor and manage jobs triggered by Google Cloud Scheduler"
        action={
          <div className="flex items-center gap-3">
            {lastUpdatedAt && (
              <span className="text-xs text-muted-foreground">
                Last updated: {lastUpdatedAt.toLocaleTimeString()}
              </span>
            )}
            <Button
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <IconRefresh size={18} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="flex gap-3 mb-6 w-full">
        <div className="flex-1 min-w-0">
          <StatCard title="Total" value={stats.total} />
        </div>
        <div className="flex-1 min-w-0">
          <StatCard title="Enabled" value={stats.enabled} />
        </div>
        <div className="flex-1 min-w-0">
          <StatCard title="Disabled" value={stats.disabled} />
        </div>
        <div className="flex-1 min-w-0">
          <StatCard title="Success" value={stats.success} />
        </div>
        <div className="flex-1 min-w-0">
          <StatCard title="Failed" value={stats.failed} />
        </div>
        <div className="flex-1 min-w-0">
          <StatCard title="Running" value={stats.running} />
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Alert className="mb-6">
        <AlertDescription>
          Jobs are triggered by Google Cloud Scheduler using OIDC authentication. 
          Manual triggers are available for testing purposes.
        </AlertDescription>
      </Alert>

      <MainCard>
        <Input
          placeholder="Search jobs by name, description, or service..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-4"
        />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Execution</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">
                  <p className="text-muted-foreground py-8">
                    {searchTerm ? 'No jobs match your search' : 'No jobs found'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              filteredJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <p className="font-semibold">{job.name}</p>
                    {job.description && (
                      <p className="text-xs text-muted-foreground">{job.description}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="gap-1">
                      {getJobTypeIcon(job.jobType)}
                      {getJobTypeLabel(job.jobType)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-mono">
                      {job.schedule || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {job.latestExecution ? (
                      <div className="flex items-center gap-1.5">
                        {getStatusIcon(job.latestExecution.status)}
                        <Badge variant={getStatusVariant(job.latestExecution.status)}>
                          {job.latestExecution.status}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Never run</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {job.latestExecution ? (
                      <div>
                        <p className="text-xs">{formatDate(job.latestExecution.startedAt)}</p>
                        <p className="text-xs text-muted-foreground">
                          Duration: {formatDuration(job.latestExecution.startedAt, job.latestExecution.completedAt)}
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setSelectedJob(job)}
                        title="View history"
                      >
                        <IconCalendarTime size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleTriggerJob(job.id)}
                        disabled={triggeringJob === job.id || job.name === 'spar-electrum-daily-markoff'}
                        title="Trigger job"
                      >
                        {triggeringJob === job.id ? (
                          <IconLoader2 size={18} className="animate-spin" />
                        ) : (
                          <IconPlayerPlay size={18} />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </MainCard>

      {/* Job History Dialog */}
      <Dialog
        open={!!selectedJob}
        onOpenChange={(open) => { if (!open) setSelectedJob(null); }}
      >
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.name} - Execution History</DialogTitle>
          </DialogHeader>

          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <IconLoader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      <p className="text-muted-foreground py-4">No execution history</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobHistory.map((execution) => (
                    <TableRow key={execution.id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(execution.status, 16)}
                          <Badge variant={getStatusVariant(execution.status)}>
                            {execution.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{formatDate(execution.startedAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{formatDate(execution.completedAt)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{formatDuration(execution.startedAt, execution.completedAt)}</span>
                      </TableCell>
                      <TableCell>
                        {execution.errorMessage ? (
                          <span
                            className="text-xs text-destructive max-w-[300px] block overflow-hidden text-ellipsis whitespace-nowrap"
                            title={execution.errorMessage}
                          >
                            {execution.errorMessage}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
